import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { BarChart2Icon, CalendarIcon, UsersIcon, TrendingUpIcon, MapPinIcon, FileTextIcon, DownloadIcon, ClockIcon, CheckIcon, XIcon, AlertTriangleIcon, UserIcon } from 'lucide-react';
import { dataService } from '../../services/DataService';
import { ServiceStatus, TrainingType } from '../../models/TrainingService';
export function ManagerInsights() {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  // Get all training services
  const allTrainings = dataService.getTrainingServices();
  // Calculate KPIs based on date range
  const getKPIs = () => {
    // Define date range
    const now = new Date();
    let startDate: Date;
    switch (dateRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
    }
    // Filter trainings by date range
    const trainingsInRange = allTrainings.filter(training => new Date(training.startTime) >= startDate && new Date(training.startTime) <= now);
    // Calculate KPIs
    const totalTrainings = trainingsInRange.length;
    const completedTrainings = trainingsInRange.filter(t => t.status === ServiceStatus.COMPLETED).length;
    const cancelledTrainings = trainingsInRange.filter(t => t.status === ServiceStatus.CANCELLED).length;
    const completionRate = totalTrainings > 0 ? completedTrainings / totalTrainings * 100 : 0;
    // Calculate instructor utilization
    const instructors = dataService.getUsersByRole('instructor');
    const instructorAssignments = instructors.map(instructor => {
      const assignedTrainings = trainingsInRange.filter(t => t.assignedUserId === instructor.id);
      return {
        id: instructor.id,
        name: instructor.name,
        assignedCount: assignedTrainings.length,
        completedCount: assignedTrainings.filter(t => t.status === ServiceStatus.COMPLETED).length,
        cancelledCount: assignedTrainings.filter(t => t.status === ServiceStatus.CANCELLED).length
      };
    }).sort((a, b) => b.assignedCount - a.assignedCount);
    // Calculate location utilization
    const projects = dataService.getProjects();
    const locationUtilization = projects.map(project => {
      const projectTrainings = trainingsInRange.filter(t => t.projectId === project.id);
      return {
        id: project.id,
        name: project.clientName,
        location: project.location.city,
        trainingCount: projectTrainings.length
      };
    }).sort((a, b) => b.trainingCount - a.trainingCount);
    // Calculate training type distribution
    const trainingTypeDistribution = Object.values(TrainingType).map(type => {
      const typeTrainings = trainingsInRange.filter(t => t.trainingType === type);
      return {
        type,
        count: typeTrainings.length,
        percentage: totalTrainings > 0 ? typeTrainings.length / totalTrainings * 100 : 0
      };
    }).sort((a, b) => b.count - a.count);
    return {
      totalTrainings,
      completedTrainings,
      cancelledTrainings,
      completionRate,
      instructorUtilization: instructorAssignments,
      locationUtilization,
      trainingTypeDistribution
    };
  };
  const kpis = getKPIs();
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  // Get upcoming trainings (next 7 days)
  const getUpcomingTrainings = () => {
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(now.getDate() + 7);
    return dataService.getTrainingServicesByDateRange(now, nextWeek).sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  };
  const upcomingTrainings = getUpcomingTrainings();
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-966">
      <div className="flex justify-between items-center" data-id="element-967">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-968">
          Management Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-969">
          <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-970">
            <option value="week" data-id="element-971">Afgelopen Week</option>
            <option value="month" data-id="element-972">Afgelopen Maand</option>
            <option value="quarter" data-id="element-973">Afgelopen Kwartaal</option>
            <option value="year" data-id="element-974">Afgelopen Jaar</option>
          </select>
          <Button variant="outline" size="sm" data-id="element-975">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-976" />
            Rapportage
          </Button>
        </div>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-977">
        <Card className="col-span-1 p-4" data-id="element-978">
          <div className="flex items-center" data-id="element-979">
            <div className="p-3 rounded-full bg-blue-100" data-id="element-980">
              <BarChart2Icon className="h-6 w-6 text-blue-600" data-id="element-981" />
            </div>
            <div className="ml-4" data-id="element-982">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-983">
                Totaal Trainingen
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-984">
                {kpis.totalTrainings}
              </p>
              <div className="flex items-center text-xs text-gray-500" data-id="element-985">
                <span className="text-green-600 font-medium" data-id="element-986">
                  {kpis.completedTrainings} afgerond
                </span>
                <span className="mx-1" data-id="element-987">|</span>
                <span className="text-red-600 font-medium" data-id="element-988">
                  {kpis.cancelledTrainings} geannuleerd
                </span>
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-989">
          <div className="flex items-center" data-id="element-990">
            <div className="p-3 rounded-full bg-green-100" data-id="element-991">
              <CheckIcon className="h-6 w-6 text-green-600" data-id="element-992" />
            </div>
            <div className="ml-4" data-id="element-993">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-994">
                Voltooiingspercentage
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-995">
                {formatPercentage(kpis.completionRate)}
              </p>
              <div className="w-full h-2 bg-gray-200 rounded-full mt-1" data-id="element-996">
                <div className="h-2 bg-green-500 rounded-full" style={{
                width: `${kpis.completionRate}%`
              }} data-id="element-997"></div>
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-998">
          <div className="flex items-center" data-id="element-999">
            <div className="p-3 rounded-full bg-purple-100" data-id="element-1000">
              <UsersIcon className="h-6 w-6 text-purple-600" data-id="element-1001" />
            </div>
            <div className="ml-4" data-id="element-1002">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1003">
                Instructeurs Ingezet
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1004">
                {kpis.instructorUtilization.filter(i => i.assignedCount > 0).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1005">
                Van {dataService.getUsersByRole('instructor').length}{' '}
                beschikbaar
              </p>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-1006">
          <div className="flex items-center" data-id="element-1007">
            <div className="p-3 rounded-full bg-yellow-100" data-id="element-1008">
              <MapPinIcon className="h-6 w-6 text-yellow-600" data-id="element-1009" />
            </div>
            <div className="ml-4" data-id="element-1010">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1011">
                Actieve Locaties
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1012">
                {kpis.locationUtilization.filter(l => l.trainingCount > 0).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1013">
                Verschillende trainingslocaties
              </p>
            </div>
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1014">
        {/* Instructor Utilization */}
        <Card title="Instructeur Inzet" className="col-span-1" actions={<Button variant="ghost" size="sm" data-id="element-1016">
              Alles Bekijken
            </Button>} data-id="element-1015">
          <div className="space-y-4" data-id="element-1017">
            {kpis.instructorUtilization.slice(0, 5).map(instructor => <div key={instructor.id} className="flex items-center justify-between" data-id="element-1018">
                <div className="flex items-center" data-id="element-1019">
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-1020">
                    <UserIcon className="h-4 w-4 text-gray-600" data-id="element-1021" />
                  </div>
                  <div className="ml-3" data-id="element-1022">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1023">
                      {instructor.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1024">
                      {instructor.assignedCount} trainingen
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2" data-id="element-1025">
                  <div className="text-xs text-green-600" data-id="element-1026">
                    {instructor.completedCount}
                  </div>
                  <div className="text-xs text-red-600" data-id="element-1027">
                    {instructor.cancelledCount}
                  </div>
                </div>
              </div>)}
            {kpis.instructorUtilization.length === 0 && <div className="text-center py-4" data-id="element-1028">
                <p className="text-sm text-gray-500" data-id="element-1029">
                  Geen instructeur data beschikbaar
                </p>
              </div>}
          </div>
        </Card>
        {/* Training Type Distribution */}
        <Card title="Training Types" className="col-span-1" actions={<Button variant="ghost" size="sm" data-id="element-1031">
              Details
            </Button>} data-id="element-1030">
          <div className="space-y-4" data-id="element-1032">
            {kpis.trainingTypeDistribution.filter(t => t.count > 0).slice(0, 5).map(typeData => <div key={typeData.type} className="space-y-1" data-id="element-1033">
                  <div className="flex justify-between items-center" data-id="element-1034">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1035">
                      {typeData.type}
                    </p>
                    <p className="text-xs font-medium text-gray-700" data-id="element-1036">
                      {typeData.count} ({formatPercentage(typeData.percentage)})
                    </p>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-1037">
                    <div className="h-2 bg-[#B61C1C] rounded-full" style={{
                width: `${typeData.percentage}%`
              }} data-id="element-1038"></div>
                  </div>
                </div>)}
            {kpis.trainingTypeDistribution.filter(t => t.count > 0).length === 0 && <div className="text-center py-4" data-id="element-1039">
                <p className="text-sm text-gray-500" data-id="element-1040">
                  Geen training type data beschikbaar
                </p>
              </div>}
          </div>
        </Card>
        {/* Location Utilization */}
        <Card title="Locatie Gebruik" className="col-span-1" actions={<Button variant="ghost" size="sm" data-id="element-1042">
              Kaart Bekijken
            </Button>} data-id="element-1041">
          <div className="space-y-3" data-id="element-1043">
            {kpis.locationUtilization.filter(l => l.trainingCount > 0).slice(0, 5).map(location => <div key={location.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md" data-id="element-1044">
                  <div data-id="element-1045">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1046">
                      {location.location}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1047">{location.name}</p>
                  </div>
                  <Badge variant="default" data-id="element-1048">
                    {location.trainingCount} trainingen
                  </Badge>
                </div>)}
            {kpis.locationUtilization.filter(l => l.trainingCount > 0).length === 0 && <div className="text-center py-4" data-id="element-1049">
                <p className="text-sm text-gray-500" data-id="element-1050">
                  Geen locatie data beschikbaar
                </p>
              </div>}
          </div>
        </Card>
      </div>
      {/* Upcoming Trainings */}
      <Card title="Aankomende Trainingen" actions={<Button variant="ghost" size="sm" data-id="element-1052">
            Volledige Planning
          </Button>} data-id="element-1051">
        <div className="overflow-x-auto" data-id="element-1053">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1054">
            <thead className="bg-gray-50" data-id="element-1055">
              <tr data-id="element-1056">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1057">
                  Datum & Tijd
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1058">
                  Training
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1059">
                  Locatie
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1060">
                  Instructeur
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1061">
                  Klant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1062">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1063">
              {upcomingTrainings.map(training => {
              const project = dataService.getProjectById(training.projectId);
              const instructor = dataService.getUserById(training.assignedUserId);
              return <tr key={training.id} className="hover:bg-gray-50" data-id="element-1064">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1065">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1066">
                        {formatDate(training.startTime)}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1067">
                        {formatTime(training.startTime)} -{' '}
                        {formatTime(training.endTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1068">
                      <div className="text-sm text-gray-900" data-id="element-1069">
                        {training.description}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1070">
                        {training.trainingType}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1071">
                      <div className="text-sm text-gray-900" data-id="element-1072">
                        {project?.location.city}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1073">
                      <div className="text-sm text-gray-900" data-id="element-1074">
                        {instructor?.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1075">
                      <div className="text-sm text-gray-900" data-id="element-1076">
                        {project?.clientName}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1077">
                        Project: {project?.projectNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1078">
                      {training.status === ServiceStatus.CONFIRMED && <Badge variant="success" data-id="element-1079">Bevestigd</Badge>}
                      {training.status === ServiceStatus.PLANNED && <Badge variant="warning" data-id="element-1080">Gepland</Badge>}
                      {training.status === ServiceStatus.IN_PROGRESS && <Badge variant="info" data-id="element-1081">In uitvoering</Badge>}
                      {training.status === ServiceStatus.COMPLETED && <Badge variant="default" data-id="element-1082">Afgerond</Badge>}
                      {training.status === ServiceStatus.CANCELLED && <Badge variant="danger" data-id="element-1083">Geannuleerd</Badge>}
                    </td>
                  </tr>;
            })}
              {upcomingTrainings.length === 0 && <tr data-id="element-1084">
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500" data-id="element-1085">
                    Geen aankomende trainingen gevonden
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}